import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { COL_NOTIFICATIONS } from '../config/collections'
import { pocketbaseErrorMessage } from '../lib/pocketbaseErrorMessage'
import { cn, linkLikeClass } from '../lib/ui'
import { pb } from '../pocketbase'
import type { NotificationRecord } from '../types/records'
import { Alert } from './ui/Alert'
import { Button } from './ui/Button'

function formatWhen(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type Props = {
  recipientId: string
  /** Компактный вид для выпадающего списка колокольчика. */
  compact?: boolean
  limit?: number
  className?: string
  onUnreadChange?: (count: number) => void
}

export function NotificationsList({
  recipientId,
  compact = false,
  limit = compact ? 8 : 50,
  className,
  onUnreadChange,
}: Props) {
  const [items, setItems] = useState<NotificationRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    if (!recipientId) {
      setItems([])
      setLoading(false)
      return
    }
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(COL_NOTIFICATIONS).getList<NotificationRecord>(1, limit, {
        filter: `recipient="${recipientId}"`,
        sort: '-id',
      })
      setItems(list.items)
      onUnreadChange?.(list.items.filter((n) => !n.read).length)
    } catch (e) {
      setErr(pocketbaseErrorMessage(e, 'Не удалось загрузить уведомления'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [recipientId, limit, onUnreadChange])

  useEffect(() => {
    void load()
  }, [load])

  const markRead = async (id: string) => {
    try {
      await pb.collection(COL_NOTIFICATIONS).update(id, { read: true })
      setItems((prev) => {
        const next = prev.map((n) => (n.id === id ? { ...n, read: true } : n))
        onUnreadChange?.(next.filter((n) => !n.read).length)
        return next
      })
    } catch {
      /* игнорируем — список обновится при следующей загрузке */
    }
  }

  const markAllRead = async () => {
    const unread = items.filter((n) => !n.read)
    await Promise.all(
      unread.map((n) => pb.collection(COL_NOTIFICATIONS).update(n.id, { read: true }).catch(() => null)),
    )
    setItems((prev) => {
      const next = prev.map((n) => ({ ...n, read: true }))
      onUnreadChange?.(0)
      return next
    })
  }

  if (loading) {
    return <p className={cn('text-sm text-su-text', className)}>Загрузка…</p>
  }

  if (err) {
    return (
      <div className={className}>
        <Alert kind="err">{err}</Alert>
        <Button size="sm" variant="ghost" onClick={() => void load()}>
          Повторить
        </Button>
      </div>
    )
  }

  if (items.length === 0) {
    return <p className={cn('text-sm text-su-text', className)}>Нет уведомлений.</p>
  }

  const unreadCount = items.filter((n) => !n.read).length

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {!compact && unreadCount > 0 ? (
        <div className="flex justify-end">
          <button type="button" className={linkLikeClass('text-xs')} onClick={() => void markAllRead()}>
            Прочитать все
          </button>
        </div>
      ) : null}
      <ul className="m-0 list-none p-0">
        {items.map((n) => {
          const body = (
            <>
              <span
                className={cn(
                  'block text-sm',
                  n.read ? 'font-normal text-su-text' : 'font-semibold text-su-text-strong',
                )}
              >
                {n.title}
              </span>
              <span className="mt-0.5 block text-xs leading-snug text-su-text">{n.message}</span>
              <span className="mt-1 block text-[0.6875rem] text-slate-400">{formatWhen(n.created)}</span>
            </>
          )

          return (
            <li
              key={n.id}
              className={cn(
                'border-b border-su-border last:border-b-0',
                compact ? 'px-3 py-2.5' : 'rounded-su border border-su-border bg-su-surface p-3 mb-2 last:mb-0',
                !n.read && 'bg-indigo-500/5',
              )}
            >
              {n.link ? (
                <Link
                  to={n.link}
                  className="block no-underline text-inherit hover:text-su-primary"
                  onClick={() => {
                    if (!n.read) void markRead(n.id)
                  }}
                >
                  {body}
                </Link>
              ) : (
                <button
                  type="button"
                  className="w-full cursor-pointer border-none bg-transparent p-0 text-left"
                  onClick={() => {
                    if (!n.read) void markRead(n.id)
                  }}
                >
                  {body}
                </button>
              )}
            </li>
          )
        })}
      </ul>
      {compact ? (
        <div className="border-t border-su-border px-3 py-2 text-center">
          <Link to="/notifications" className={linkLikeClass('text-xs')}>
            Все уведомления
          </Link>
        </div>
      ) : null}
    </div>
  )
}
